/**
 * TERMOS ADITIVOS DE UM CONTRATO — o que a aba precisa saber e a tela não deve
 * decidir sozinha.
 *
 * Espelha as regras puras de `helpers/aditivo-contrato.util.ts` do backend
 * (#236). Aqui só se LÊ: o número é gerado lá, a validação de vínculo é lá, e o
 * front nunca inventa ordinal — sequencial gerado no cliente duplica quando
 * duas abas criam ao mesmo tempo.
 */

/** O que o aditivo FAZ. É lista, não valor único. */
export const ADITIVO_SUBTIPOS = [
  'renovacao',
  'reajuste',
  'alteracao_valor',
  'alteracao_objeto',
  'alteracao_parte',
  'outro',
] as const;
export type AditivoSubtipo = (typeof ADITIVO_SUBTIPOS)[number];

/**
 * Rótulos da tela. O caso do Sicoob é o motivo de o campo ser lista: o mesmo
 * instrumento prorroga 24 meses, aplica IPCA de 4,64% e inclui a cláusula de
 * reajuste anual que o contrato original não tem. Campo único obrigaria a
 * escolher qual das três mentir.
 */
export const ROTULO_DO_SUBTIPO: Record<AditivoSubtipo, string> = {
  renovacao: 'Renovação',
  reajuste: 'Reajuste',
  alteracao_valor: 'Alteração de valor',
  alteracao_objeto: 'Alteração de objeto',
  alteracao_parte: 'Alteração de parte',
  outro: 'Outro',
};

export function rotuloDoSubtipo(v: string): string {
  return (ROTULO_DO_SUBTIPO as Record<string, string>)[v] || v;
}

export interface Aditivo {
  id: number;
  contractId?: number | null;
  documentKind?: string | null;
  aditivoSubtipos?: string[] | null;
  ordinal?: number | null;
  numeroNoDocumento?: string | null;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  status?: string | null;
  signedAt?: string | null;
  [k: string]: any;
}

/**
 * COMO A LINHA SE CHAMA.
 *
 * ⚠️ O número do PAPEL vence o número do sistema. No Sicoob o registro é
 * `ordinal: 1` e o documento diz "2º", porque o Anexo A do contrato original
 * também se chama termo aditivo e ninguém vai renumerar o que já foi assinado.
 * Mostrar o ordinal faria a tela discordar do documento que está na mão do
 * cliente.
 */
export function numeroDoAditivo(a: Aditivo): string {
  const doPapel = String(a?.numeroNoDocumento ?? '').trim();
  if (doPapel) return doPapel;
  const ord = Number(a?.ordinal);
  return ord > 0 ? `${ord}º` : '—';
}

export const ehAditivo = (a: Aditivo): boolean => a?.documentKind === 'aditivo';

/**
 * O ADITIVO VIGENTE: o assinado de vigência mais longa.
 *
 * Só conta ASSINADO. Rascunho e aguardando assinatura são intenção, não
 * vigência — e um rascunho mandando na data do cabeçalho faria o contrato
 * parecer renovado antes de alguém assinar.
 *
 * Devolve `null` quando não há nenhum, e aí a vigência do contrato é a dele
 * mesmo. É por isso que a data é DERIVADA na leitura e não copiada para
 * `contractEndDate`: dado derivado guardado em dois lugares diverge, e o
 * contrato 140 com o campo vazio depois de duas prorrogações é o sintoma.
 */
export function aditivoVigente(aditivos: Aditivo[]): Aditivo | null {
  const assinados = (aditivos || []).filter(
    (a) => ehAditivo(a) && a.status === 'Assinado' && !!a.vigenciaFim,
  );
  if (!assinados.length) return null;
  return assinados.reduce((melhor, a) =>
    String(a.vigenciaFim) > String(melhor.vigenciaFim) ? a : melhor,
  );
}

export interface VigenciaEfetiva {
  fim: string | null;
  /** De onde a data veio — a coluna que o vendedor precisa para confiar nela. */
  fonte: string;
}

/**
 * A vigência que VALE hoje, e de onde ela veio.
 *
 * A fonte importa mais do que parece: as origens são heterogêneas (contrato,
 * proposta aprovada, e-mail, ou derivação sem cláusula expressa) e o cabeçalho
 * que não diz a origem faz a data parecer sempre documental.
 */
export function vigenciaEfetiva(
  contrato: { endDate?: string | null; contractEndDate?: string | null } | null | undefined,
  aditivos: Aditivo[],
): VigenciaEfetiva {
  const vigente = aditivoVigente(aditivos);
  if (vigente) {
    return { fim: String(vigente.vigenciaFim), fonte: `${numeroDoAditivo(vigente)} Termo Aditivo` };
  }
  const doContrato = contrato?.contractEndDate || contrato?.endDate || null;
  if (doContrato) return { fim: String(doContrato), fonte: 'contrato' };
  return { fim: null, fonte: 'sem data de fim' };
}
